import { View, Text, StyleSheet, Font } from "@react-pdf/renderer";
import regular from "@packages/assets/font-family/Montserrat-Regular.ttf";
import bold from "@packages/assets/font-family/Montserrat-Bold.ttf";
import { useTranslation } from "react-i18next";
import { PdfLayout } from "../pdf/PdfLayout";
import { commonStyles } from "../pdf/CommonPdfStyles";
import { HomeworkIdeaCardProps } from "./HomeworkIdeaCard";
import MarkdownRenderer from "../../../tools/components/custom-markdown";
import {
  HomeworkIdeaResponseDto,
  HomeworkIdeaItem,
} from "@packages/api/madlenAPI.schemas";
import { PdfCustomizationSettings } from "@features/common/components/pdf-customization-modal";
import { scaleFontSize } from "../pdf/fontSizeAdjustment";
Font.register({
  family: "Montserrat",
  fonts: [{ src: regular }, { src: bold, fontWeight: "bold" }],
});

const styles = StyleSheet.create({
  ideaContainer: {
    marginBottom: 15,
    padding: 12,
    backgroundColor: "#f0fdf4",
    borderRadius: 8,
  },
  ideaTitle: {
    fontSize: 14,
    fontWeight: "bold",
    color: "#16a34a",
    marginBottom: 8,
  },
  badgeContainer: {
    flexDirection: "row",
    marginBottom: 8,
  },
  complexityBadge: {
    alignSelf: "flex-start",
    backgroundColor: "#ecfccb", // lime-100
    color: "#3f6212", // lime-800
    padding: "4 8",
    borderRadius: 4,
    fontSize: 9,
    marginBottom: 4,
  },
  timeBadge: {
    alignSelf: "flex-start",
    backgroundColor: "#f3e8ff", // purple-100
    color: "#6b21a8", // purple-800
    padding: "4 8",
    borderRadius: 4,
    fontSize: 9,
    marginBottom: 4,
    marginLeft: 4,
  },
  description: {
    fontSize: 10,
    color: "#374151",
    lineHeight: 1.4,
    marginBottom: 8,
  },
  resourceItem: {
    flexDirection: "row",
    marginBottom: 4,
    paddingLeft: 8,
  },
  bullet: {
    width: 12,
    fontSize: 11,
    color: "#16a34a",
  },
  sectionTitle: {
    fontSize: 12,
    fontWeight: "bold",
    color: "#16a34a",
    marginTop: 12,
    marginBottom: 8,
  },
  subSectionTitle: {
    fontSize: 10,
    fontWeight: "bold",
    color: "#4b5563",
    marginTop: 8,
    marginBottom: 6,
  },
  sectionContainer: {
    borderLeftWidth: 4,
    paddingLeft: 8,
    marginBottom: 12,
  },
  teacherSection: {
    backgroundColor: "#f7fee7", // lime-50
    padding: 8,
    borderRadius: 4,
    marginBottom: 12,
    borderLeftWidth: 4,
    borderLeftColor: "#a3e635", // lime-400
  },
  studentSection: {
    backgroundColor: "#eff6ff", // blue-50
    padding: 8,
    borderRadius: 4,
    marginBottom: 12,
    borderLeftWidth: 4,
    borderLeftColor: "#60a5fa", // blue-400
  },
  criteriaContainer: {
    flexDirection: "row",
    marginBottom: 8,
    alignItems: "flex-start",
  },
  criteriaIcon: {
    width: 16,
    height: 16,
    borderRadius: 8,
    backgroundColor: "#bfdbfe", // blue-200
    color: "#1d4ed8", // blue-700
    textAlign: "center",
    fontSize: 8,
    marginRight: 6,
    marginTop: 2,
  },
  teacherIcon: {
    backgroundColor: "#d9f99d", // lime-200
    color: "#4d7c0f", // lime-700
  },
});

const HomeworkIdeaDocument = ({
  homeworkIdea,
  customizationSettings,
}: {
  homeworkIdea: HomeworkIdeaResponseDto & { reportIdeas?: HomeworkIdeaItem[] };
  customizationSettings: PdfCustomizationSettings;
}) => {
  const { t } = useTranslation();

  // Handle backward compatibility - use reportIdeas if available
  const ideas = homeworkIdea.reportIdeas || [];

  return (
    <PdfLayout
      courseName={homeworkIdea.courseName || ""}
      gradeLevel={homeworkIdea.gradeLevel || 0}
      topic={homeworkIdea.title || homeworkIdea.prompt || ""}
      imageUrl={homeworkIdea.imageUrl}
      author={homeworkIdea.authorName}
      createdDate={homeworkIdea.createdAt}
      headerTextColor="#16a34a"
      customizationSettings={customizationSettings}
    >
      {ideas.map((idea, index) => (
        <View key={index} style={styles.ideaContainer} break={index > 0}>
          <MarkdownRenderer
            customStyle={{
              text: {
                text: {
                  ...styles.ideaTitle,
                  fontSize: scaleFontSize(
                    styles.ideaTitle.fontSize,
                    customizationSettings?.fontSize,
                  ),
                },
                container: {
                  marginBottom: 8,
                },
              },
            }}
            content={idea.title || ""}
          />

          {/* Badges container */}
          <View style={styles.badgeContainer}>
            {idea.complexity_level && (
              <Text
                style={[
                  styles.complexityBadge,
                  {
                    fontSize: scaleFontSize(
                      styles.complexityBadge.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                ]}
              >
                {t(`homeworkIdeas.complexity.${idea.complexity_level}`)}
              </Text>
            )}

            {/* Estimated time badge */}
            {idea.estimated_time && (
              <Text
                style={[
                  styles.timeBadge,
                  {
                    fontSize: scaleFontSize(
                      styles.timeBadge.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                ]}
              >
                {idea.estimated_time}
              </Text>
            )}
          </View>

          {/* Main Description */}
          <MarkdownRenderer
            customStyle={{
              text: {
                text: {
                  ...styles.description,
                  fontSize: scaleFontSize(
                    styles.description.fontSize,
                    customizationSettings?.fontSize,
                  ),
                },
                container: {
                  marginBottom: 8,
                },
              },
            }}
            content={idea.description || ""}
          />

          {/* Purpose Alignment Section */}
          {idea.purpose_alignment && (
            <View wrap={false}>
              <Text
                style={[
                  styles.sectionTitle,
                  {
                    fontSize: scaleFontSize(
                      styles.sectionTitle.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                ]}
              >
                {t("homeworkIdeas.purposeAlignment")}
              </Text>
              <View
                style={[
                  styles.sectionContainer,
                  { borderLeftColor: "#3b82f6" }, // blue-500
                ]}
              >
                <MarkdownRenderer
                  customStyle={{
                    text: {
                      text: {
                        ...styles.description,
                        fontSize: scaleFontSize(
                          styles.description.fontSize,
                          customizationSettings?.fontSize,
                        ),
                      },
                      container: {
                        marginBottom: 8,
                      },
                    },
                  }}
                  content={idea.purpose_alignment}
                />
              </View>
            </View>
          )}

          {/* Outline Section */}
          {idea.outline && (
            <View wrap={false} break={idea.purpose_alignment ? false : true}>
              <Text
                style={[
                  styles.sectionTitle,
                  {
                    fontSize: scaleFontSize(
                      styles.sectionTitle.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                ]}
              >
                {t("homeworkIdeas.outline")}
              </Text>
              <View
                style={[
                  styles.sectionContainer,
                  { borderLeftColor: "#6b7280" }, // gray-500
                ]}
              >
                <MarkdownRenderer
                  customStyle={{
                    text: {
                      text: {
                        ...commonStyles.sectionContent,
                        fontSize: scaleFontSize(
                          commonStyles.sectionContent.fontSize,
                          customizationSettings?.fontSize,
                        ),
                      },
                      container: {
                        marginBottom: 8,
                      },
                    },
                  }}
                  content={idea.outline}
                />
              </View>
            </View>
          )}

          {/* Resources Section */}
          {(idea.resources || idea.recommended_resources) && (
            <View wrap={false} break={idea.outline ? true : false}>
              <Text
                style={[
                  styles.sectionTitle,
                  {
                    fontSize: scaleFontSize(
                      styles.sectionTitle.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                ]}
              >
                {t("homeworkIdeas.resources")}
              </Text>

              {/* Modern resources structure */}
              {idea.resources && (
                <View
                  style={[
                    styles.sectionContainer,
                    { borderLeftColor: "#6b7280" }, // gray-500
                  ]}
                >
                  {/* Digital Resources */}
                  {idea.resources.digital_resources &&
                    idea.resources.digital_resources.length > 0 && (
                      <View style={{ marginBottom: 10 }} wrap={false}>
                        <Text
                          style={[
                            styles.subSectionTitle,
                            {
                              fontSize: scaleFontSize(
                                styles.subSectionTitle.fontSize,
                                customizationSettings?.fontSize,
                              ),
                            },
                          ]}
                        >
                          {t("homeworkIdeas.digitalResources")}
                        </Text>
                        {idea.resources.digital_resources.map(
                          (resource, idx) => (
                            <View key={idx} style={styles.resourceItem}>
                              <Text style={styles.bullet}>•</Text>
                              <Text
                                style={[
                                  styles.description,
                                  {
                                    fontSize: scaleFontSize(
                                      styles.description.fontSize,
                                      customizationSettings?.fontSize,
                                    ),
                                  },
                                ]}
                              >
                                {resource}
                              </Text>
                            </View>
                          ),
                        )}
                      </View>
                    )}

                  {/* Printed Materials */}
                  {idea.resources.printed_materials &&
                    idea.resources.printed_materials.length > 0 && (
                      <View style={{ marginBottom: 10 }} wrap={false}>
                        <Text
                          style={[
                            styles.subSectionTitle,
                            {
                              fontSize: scaleFontSize(
                                styles.subSectionTitle.fontSize,
                                customizationSettings?.fontSize,
                              ),
                            },
                          ]}
                        >
                          {t("homeworkIdeas.printedMaterials")}
                        </Text>
                        {idea.resources.printed_materials.map(
                          (resource, idx) => (
                            <View key={idx} style={styles.resourceItem}>
                              <Text style={styles.bullet}>•</Text>
                              <Text
                                style={[
                                  styles.description,
                                  {
                                    fontSize: scaleFontSize(
                                      styles.description.fontSize,
                                      customizationSettings?.fontSize,
                                    ),
                                  },
                                ]}
                              >
                                {resource}
                              </Text>
                            </View>
                          ),
                        )}
                      </View>
                    )}

                  {/* Tools and Materials */}
                  {idea.resources.tools_and_materials &&
                    idea.resources.tools_and_materials.length > 0 && (
                      <View style={{ marginBottom: 10 }}>
                        <Text
                          style={[
                            styles.subSectionTitle,
                            {
                              fontSize: scaleFontSize(
                                styles.subSectionTitle.fontSize,
                                customizationSettings?.fontSize,
                              ),
                            },
                          ]}
                        >
                          {t("homeworkIdeas.toolsAndMaterials")}
                        </Text>
                        {idea.resources.tools_and_materials.map(
                          (resource, idx) => (
                            <View key={idx} style={styles.resourceItem}>
                              <Text style={styles.bullet}>•</Text>
                              <Text
                                style={[
                                  styles.description,
                                  {
                                    fontSize: scaleFontSize(
                                      styles.description.fontSize,
                                      customizationSettings?.fontSize,
                                    ),
                                  },
                                ]}
                              >
                                {resource}
                              </Text>
                            </View>
                          ),
                        )}
                      </View>
                    )}
                </View>
              )}

              {/* Legacy recommended resources */}
              {idea.recommended_resources &&
                idea.recommended_resources.length > 0 && (
                  <View
                    style={[
                      styles.sectionContainer,
                      { borderLeftColor: "#6b7280" }, // gray-500
                    ]}
                    wrap={true}
                  >
                    {idea.recommended_resources.map((resource, idx) => (
                      <View key={idx} style={styles.resourceItem}>
                        <Text style={styles.bullet}>{idx + 1}.</Text>
                        <MarkdownRenderer
                          customStyle={{
                            text: {
                              text: {
                                fontSize: scaleFontSize(
                                  9,
                                  customizationSettings?.fontSize,
                                ),
                              },
                            },
                          }}
                          content={resource}
                        />
                      </View>
                    ))}
                  </View>
                )}
            </View>
          )}

          {/* Teacher Guidelines Section */}
          {idea.teacher_guidelines &&
            ((idea.teacher_guidelines.feedback_tips?.length ?? 0) > 0 ||
              (idea.teacher_guidelines.grading_criteria?.length ?? 0) > 0 ||
              idea.teacher_guidelines.time_estimation) && (
              <View
                wrap={idea.recommended_resources ? true : false}
                break={!idea.recommended_resources && true}
              >
                <Text
                  style={[
                    styles.sectionTitle,
                    {
                      fontSize: scaleFontSize(
                        styles.sectionTitle.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                  ]}
                >
                  {t("homeworkIdeas.teacherGuidelines")}
                </Text>
                <View style={styles.teacherSection} wrap={false}>
                  {/* Feedback Tips */}
                  {idea.teacher_guidelines.feedback_tips &&
                    idea.teacher_guidelines.feedback_tips.length > 0 && (
                      <View style={{ marginBottom: 10 }}>
                        <Text
                          style={[
                            styles.subSectionTitle,
                            {
                              fontSize: scaleFontSize(
                                styles.subSectionTitle.fontSize,
                                customizationSettings?.fontSize,
                              ),
                              color: "#65a30d", // lime-600
                            },
                          ]}
                        >
                          {t("homeworkIdeas.feedbackTips")}
                        </Text>
                        {idea.teacher_guidelines.feedback_tips.map(
                          (tip, idx) => (
                            <View key={idx} style={styles.criteriaContainer}>
                              <Text
                                style={[
                                  styles.criteriaIcon,
                                  styles.teacherIcon,
                                ]}
                              >
                                {idx + 1}
                              </Text>
                              <Text
                                style={[
                                  styles.description,
                                  {
                                    fontSize: scaleFontSize(
                                      styles.description.fontSize,
                                      customizationSettings?.fontSize,
                                    ),
                                    flex: 1,
                                  },
                                ]}
                              >
                                {tip}
                              </Text>
                            </View>
                          ),
                        )}
                      </View>
                    )}

                  {/* Grading Criteria */}
                  {idea.teacher_guidelines.grading_criteria &&
                    idea.teacher_guidelines.grading_criteria.length > 0 && (
                      <View style={{ marginBottom: 10 }}>
                        <Text
                          style={[
                            styles.subSectionTitle,
                            {
                              fontSize: scaleFontSize(
                                styles.subSectionTitle.fontSize,
                                customizationSettings?.fontSize,
                              ),
                              color: "#4d7c0f",
                            },
                          ]}
                        >
                          {t("homeworkIdeas.gradingCriteria")}
                        </Text>
                        {idea.teacher_guidelines.grading_criteria.map(
                          (criteria, idx) => (
                            <View key={idx} style={styles.criteriaContainer}>
                              <Text
                                style={[
                                  styles.criteriaIcon,
                                  styles.teacherIcon,
                                ]}
                              >
                                {idx + 1}
                              </Text>
                              <Text
                                style={[
                                  styles.description,
                                  {
                                    fontSize: scaleFontSize(
                                      styles.description.fontSize,
                                      customizationSettings?.fontSize,
                                    ),
                                    flex: 1,
                                  },
                                ]}
                              >
                                {criteria}
                              </Text>
                            </View>
                          ),
                        )}
                      </View>
                    )}

                  {/* Time Estimation */}
                  {idea.teacher_guidelines.time_estimation && (
                    <View style={{ marginBottom: 10 }}>
                      <Text
                        style={[
                          styles.subSectionTitle,
                          {
                            fontSize: scaleFontSize(
                              styles.subSectionTitle.fontSize,
                              customizationSettings?.fontSize,
                            ),
                            color: "#65a30d", // lime-600
                          },
                        ]}
                      >
                        {t("homeworkIdeas.timeEstimation")}
                      </Text>
                      <Text
                        style={[
                          styles.description,
                          {
                            fontSize: scaleFontSize(
                              styles.description.fontSize,
                              customizationSettings?.fontSize,
                            ),
                            marginLeft: 8,
                          },
                        ]}
                      >
                        {idea.teacher_guidelines.time_estimation}
                      </Text>
                    </View>
                  )}
                </View>
              </View>
            )}

          {/* Student Criteria Section */}
          {idea.student_criteria &&
            ((idea.student_criteria.self_assessment_questions ?? []).length >
              0 ||
              (idea.student_criteria.success_indicators ?? []).length > 0) && (
              <View wrap={true} break={true}>
                <Text
                  style={[
                    styles.sectionTitle,
                    {
                      fontSize: scaleFontSize(
                        styles.sectionTitle.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                  ]}
                >
                  {t("homeworkIdeas.studentCriteria")}
                </Text>
                <View style={styles.studentSection} wrap={true}>
                  {/* Self Assessment Questions */}
                  {idea.student_criteria.self_assessment_questions &&
                    idea.student_criteria.self_assessment_questions.length >
                      0 && (
                      <View style={{ marginBottom: 10 }}>
                        <Text
                          style={[
                            styles.subSectionTitle,
                            {
                              fontSize: scaleFontSize(
                                styles.subSectionTitle.fontSize,
                                customizationSettings?.fontSize,
                              ),
                              color: "#2563eb", // blue-600
                            },
                          ]}
                        >
                          {t("homeworkIdeas.selfAssessmentQuestions")}
                        </Text>
                        {idea.student_criteria.self_assessment_questions.map(
                          (question, idx) => (
                            <View key={idx} style={styles.criteriaContainer}>
                              <Text style={styles.criteriaIcon}>{idx + 1}</Text>
                              <Text
                                style={[
                                  styles.description,
                                  {
                                    fontSize: scaleFontSize(
                                      styles.description.fontSize,
                                      customizationSettings?.fontSize,
                                    ),
                                    flex: 1,
                                  },
                                ]}
                              >
                                {question}
                              </Text>
                            </View>
                          ),
                        )}
                      </View>
                    )}

                  {/* Success Indicators */}
                  {idea.student_criteria.success_indicators &&
                    idea.student_criteria.success_indicators.length > 0 && (
                      <View style={{ marginBottom: 10 }}>
                        <Text
                          style={[
                            styles.subSectionTitle,
                            {
                              fontSize: scaleFontSize(
                                styles.subSectionTitle.fontSize,
                                customizationSettings?.fontSize,
                              ),
                              color: "#2563eb", // blue-600
                            },
                          ]}
                        >
                          {t("homeworkIdeas.successIndicators")}
                        </Text>
                        {idea.student_criteria.success_indicators.map(
                          (indicator, idx) => (
                            <View key={idx} style={styles.criteriaContainer}>
                              <Text style={styles.criteriaIcon}>✓</Text>
                              <Text
                                style={[
                                  styles.description,
                                  {
                                    fontSize: scaleFontSize(
                                      styles.description.fontSize,
                                      customizationSettings?.fontSize,
                                    ),
                                    flex: 1,
                                  },
                                ]}
                              >
                                {indicator}
                              </Text>
                            </View>
                          ),
                        )}
                      </View>
                    )}
                </View>
              </View>
            )}
        </View>
      ))}
    </PdfLayout>
  );
};

function HomeworkIdeaCardPDF(
  props: Omit<HomeworkIdeaCardProps, "onCreateAgain">,
) {
  return <HomeworkIdeaDocument {...props} />;
}

export default HomeworkIdeaCardPDF;
